import * as B from '@blueprintjs/core';
import {History} from 'history';
import React from 'react';
import {useHistory} from 'react-router-dom';
import {uuid} from 'short-uuid';

import {ApiFeature} from 'app/modules/Remote/Feature/types';
import {useUserInfo} from 'app/providers/AuthProvider';
import {formatDuration} from 'app/utils/dateUtils';
import {alphanumericSort, formatFeatureName} from 'app/utils/featureUtils';

import {ApiAgentWorkflow} from './hooks';
import * as cs from './styles.styl';

type Props = {
  workflow: ApiAgentWorkflow;
  features: ApiFeature[];
  history: History;
};

export const AgentWorkflow: React.FC<Props> = ({workflow, features}) => {
  const workflowFeatures = features.filter((f) => workflow.featureIds.includes(f.id));
  const [organization] = useUserInfo();
  const history = useHistory();

  return (
    <div className={cs.page}>
      <div className={cs.pageContent}>
        <B.Breadcrumbs
          items={[
            {
              text: 'Workflows',
              onClick: () => {
                history.push(`/${organization.get('id')}/agent/workflows`);
              },
            },
            {
              text: workflow.name,
              onClick: () => {
                history.push(`/${organization.get('id')}/agent/workflows/${workflow.id}`);
              },
            },
          ]}
        />

        <B.Card style={{width: 'min-content'}}>
          <h3 style={{marginTop: '0px', letterSpacing: 'none'}}>Workflow details</h3>
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
            <div>
              <span className={cs.label}>Cadence</span>
              {workflow.cadence.charAt(0).toUpperCase() + workflow.cadence.slice(1)}
            </div>
            <div>
              <span className={cs.label}>Description</span>
              {workflow.description}
            </div>
            <div>
              <span className={cs.label}>Next run</span>
              Sept 1, 2025
            </div>
            <div>
              <div className={cs.label} style={{marginTop: '1rem', marginBottom: '0.5rem'}}>
                Features
              </div>
              <div className={cs.featureTags}>
                {workflowFeatures
                  .map((feature) => formatFeatureName(feature))
                  .sort(alphanumericSort)
                  .map((formattedFeatureName, index) => (
                    <B.Tag key={index} minimal={true} large={true}>
                      {formattedFeatureName}
                    </B.Tag>
                  ))}
              </div>
            </div>
          </div>
        </B.Card>

        <B.Section
          title="Runs"
          rightElement={
            <B.Button
              text="Run now"
              intent="primary"
              icon="clean"
              style={{margin: '1.5rem 0'}}
              className={cs.agentAction}
              onClick={() => {
                const runId = uuid();
                history.push(
                  `/${organization.get('id')}/agent/workflows/${workflow.id}/runs/${runId}?new=true`
                );
              }}
            />
          }
        >
          <B.CardList bordered={false}>
            {workflow.runs
              .sort((a, b) => (b.startedAt > a.startedAt ? 1 : -1))
              .map((run) => {
                const now = new Date();
                const start = new Date(run.startedAt);
                const end = run.endedAt ? new Date(run.endedAt) : null;

                return (
                  <B.Card
                    key={run.runId}
                    style={{justifyContent: 'space-between'}}
                    interactive={true}
                    onClick={() => {
                      history.push(
                        `/${organization.get('id')}/agent/workflows/${workflow.id}/runs/${run.runId}`
                      );
                    }}
                  >
                    <div>
                      {run.status === 'completed' && (
                        <>
                          <B.Icon
                            className={cs.icon}
                            icon="tick-circle"
                            intent="success"
                            size={20}
                          />
                          {run.numFindings} Findings
                        </>
                      )}
                      {run.status === 'failed' && (
                        <>
                          <B.Icon
                            className={cs.icon}
                            icon="cross-circle"
                            intent="danger"
                            size={20}
                          />
                          Run failed
                        </>
                      )}
                      {run.status === 'in_progress' && (
                        <>
                          <B.Spinner className={cs.icon} size={20} />
                          In Progress
                        </>
                      )}
                    </div>

                    <div
                      style={{
                        display: 'flex',
                      }}
                    >
                      <div className={cs.runTimeContainer}>
                        <div className={cs.runTime}>
                          <B.Icon icon="calendar" /> {formatDuration(start, now)} ago
                        </div>
                        {end && (
                          <div className={cs.runTime}>
                            <B.Icon icon="time" /> {formatDuration(start, end)}
                          </div>
                        )}
                      </div>

                      <B.Icon icon="chevron-right" />
                    </div>
                  </B.Card>
                );
              })}
          </B.CardList>
        </B.Section>
      </div>
    </div>
  );
};
